import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Shield, Lock, FileText, CheckCircle } from 'lucide-react';
import { mockData } from '../data/mock';

const ComplianceSection = () => {
  const { compliance } = mockData;

  const iconMap = {
    'SOC 2 Type II': Shield,
    'GDPR Compliant': Lock,
    'RNG Provenance': CheckCircle,
    'KYC Integration': FileText
  };

  const colorMap = {
    'SOC 2 Type II': 'bg-blue-100 text-blue-600',
    'GDPR Compliant': 'bg-green-100 text-green-600', 
    'RNG Provenance': 'bg-purple-100 text-purple-600',
    'KYC Integration': 'bg-orange-100 text-orange-600'
  };

  return (
    <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-grid-white/[0.05] [mask-image:linear-gradient(0deg,#000,rgba(0,0,0,0.6))]" />
      
      <div className="container mx-auto px-4 relative">
        <div className="text-center mb-16">
          <Badge variant="secondary" className="mb-4 bg-white/10 text-white border-white/20">
            Security & Compliance
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            {compliance.title}
          </h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Built with security-first principles and compliance at every level. 
            Your data and your customers' trust are our top priorities.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {compliance.features.map((feature, index) => {
            const IconComponent = iconMap[feature.title];
            const colorClass = colorMap[feature.title];
            
            return (
              <Card key={index} className="bg-white/5 backdrop-blur-sm border-white/10 hover:bg-white/10 transition-all duration-300">
                <CardHeader className="pb-4">
                  <div className={`p-3 rounded-lg w-fit ${colorClass.replace('text-', 'bg-').replace('-600', '-500/20')} mb-4`}>
                    <IconComponent className={`w-8 h-8 ${colorClass.split(' ')[1]}`} />
                  </div>
                  <CardTitle className="text-white text-lg">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-blue-100">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Additional compliance info */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center space-x-6 bg-white/10 backdrop-blur-sm rounded-2xl px-8 py-4 border border-white/20">
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-green-400" />
              <span className="text-white font-medium">ISO 27001 Certified</span>
            </div>
            <div className="w-px h-6 bg-white/20" />
            <div className="flex items-center space-x-2">
              <Lock className="w-5 h-5 text-blue-400" />
              <span className="text-white font-medium">256-bit Encryption</span>
            </div>
            <div className="w-px h-6 bg-white/20" />
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-purple-400" />
              <span className="text-white font-medium">99.99% Uptime SLA</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComplianceSection;